import React, { useState, useEffect, useCallback } from "react";
import {
    intl,
    fetchData,
    Notifications,
    HTTP_STATUS,
} from "dmsbasereact";
import { AlertSmtpSettingsCard } from "dmsalertsystemreact";
import {
    Box,
    Typography,
    Switch,
    FormControlLabel,
    Alert,
    Card,
    CardContent,
    Divider,
    CircularProgress,
    Grid,
    Chip,
} from "@mui/material";
import {
    BugReport as DebugIcon,
    LocalHospital as CfmIcon,
    Store as SapIcon,
    Schedule as CycleIcon,
} from "@mui/icons-material";

export const DebugSettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});
    const [alertSmtpDebug, setAlertSmtpDebug] = useState(false);
    const [cfmDebug, setCfmDebug] = useState(false);
    const [sapBuyerSkip, setSapBuyerSkip] = useState(false);
    const [noCheckoutForceRun, setNoCheckoutForceRun] = useState(false);
    const [docExpiryForceRun, setDocExpiryForceRun] = useState(false);

    const fetchAllDebugSettings = useCallback(async () => {
        setLoading(true);
        try {
            const [alertSmtpRes, cfmRes, sapBuyerRes, noCheckoutRes, docExpiryRes] = await Promise.all([
                fetchData("/alert_smtp_settings/debug-mode", "GET", true).catch(() => null),
                fetchData("/cfm_api_settings/debug-mode", "GET", true).catch(() => null),
                fetchData("/debug_settings/skip-sap-buyer-validation", "GET", true).catch(() => null),
                fetchData("/debug_settings/no-checkout-force-run", "GET", true).catch(() => null),
                fetchData("/debug_settings/doc-expiry-force-run", "GET", true).catch(() => null),
            ]);

            const alertSmtpData = alertSmtpRes?.data?.data || alertSmtpRes?.data || {};
            const cfmData = cfmRes?.data?.data || cfmRes?.data || {};
            const sapBuyerData = sapBuyerRes?.data?.data || sapBuyerRes?.data || {};
            const noCheckoutData = noCheckoutRes?.data?.data || noCheckoutRes?.data || {};
            const docExpiryData = docExpiryRes?.data?.data || docExpiryRes?.data || {};

            setAlertSmtpDebug(alertSmtpData.debug_mode || false);
            setCfmDebug(cfmData.debug_mode || false);
            setSapBuyerSkip(sapBuyerData.enabled || false);
            setNoCheckoutForceRun(noCheckoutData.enabled || false);
            setDocExpiryForceRun(docExpiryData.enabled || false);
        } catch (error) {
            Notifications.toast(
                intl.get("DebugSettings.FetchError").d("Error loading debug settings"),
                "error"
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAllDebugSettings();
    }, [fetchAllDebugSettings]);

    const handleAlertSmtpToggle = (newValue) => {
        setAlertSmtpDebug(newValue);
    };

    const handleToggleCfmDebug = async () => {
        const newValue = !cfmDebug;
        setSaving(prev => ({ ...prev, cfm_debug: true }));

        try {
            const response = await fetchData("/cfm_api_settings/debug-mode", "PUT", true, {
                debug_mode: newValue
            });

            if (response.status === HTTP_STATUS.OK_200) {
                setCfmDebug(newValue);
                Notifications.toast(
                    newValue
                        ? intl.get("DebugSettings.CfmDebugEnabled").d("CFM Debug enabled - validations will return simulated data")
                        : intl.get("DebugSettings.CfmDebugDisabled").d("CFM Debug disabled - validations will use real API"),
                    "success"
                );
            } else {
                throw new Error("Update failed");
            }
        } catch (error) {
            Notifications.toast(
                intl.get("DebugSettings.UpdateError").d("Error updating debug mode"),
                "error"
            );
        } finally {
            setSaving(prev => ({ ...prev, cfm_debug: false }));
        }
    };

    const handleToggleSapBuyerSkip = async () => {
        setSaving(prev => ({ ...prev, sap_buyer: true }));

        try {
            const response = await fetchData("/debug_settings/skip-sap-buyer-validation", "PUT", true);

            if (response.status === HTTP_STATUS.OK_200) {
                const data = response?.data?.data || response?.data || {};
                setSapBuyerSkip(data.enabled || false);
                Notifications.toast(
                    data.enabled
                        ? intl.get("DebugSettings.SapBuyer.Enabled").d("SAP buyer validation skip enabled - Buyer institutions can be created without SAP registration")
                        : intl.get("DebugSettings.SapBuyer.Disabled").d("SAP buyer validation skip disabled - Buyer institutions require SAP registration"),
                    "success"
                );
            } else {
                throw new Error("Update failed");
            }
        } catch (error) {
            Notifications.toast(
                intl.get("DebugSettings.UpdateError").d("Error updating debug mode"),
                "error"
            );
        } finally {
            setSaving(prev => ({ ...prev, sap_buyer: false }));
        }
    };

    const handleToggleNoCheckoutForceRun = async () => {
        setSaving(prev => ({ ...prev, no_checkout: true }));

        try {
            const response = await fetchData("/debug_settings/no-checkout-force-run", "PUT", true);

            if (response.status === HTTP_STATUS.OK_200) {
                const data = response?.data?.data || response?.data || {};
                setNoCheckoutForceRun(data.enabled || false);
                Notifications.toast(
                    data.enabled
                        ? intl.get("DebugSettings.NoCheckout.Enabled").d("No Checkout cycle force run enabled - cycle will run every 60 seconds")
                        : intl.get("DebugSettings.NoCheckout.Disabled").d("No Checkout cycle force run disabled - cycle will run daily at 00:01"),
                    "success"
                );
            } else {
                throw new Error("Update failed");
            }
        } catch (error) {
            Notifications.toast(
                intl.get("DebugSettings.UpdateError").d("Error updating debug mode"),
                "error"
            );
        } finally {
            setSaving(prev => ({ ...prev, no_checkout: false }));
        }
    };

    const handleToggleDocExpiryForceRun = async () => {
        setSaving(prev => ({ ...prev, doc_expiry: true }));

        try {
            const response = await fetchData("/debug_settings/doc-expiry-force-run", "PUT", true);

            if (response.status === HTTP_STATUS.OK_200) {
                const data = response?.data?.data || response?.data || {};
                setDocExpiryForceRun(data.enabled || false);
                Notifications.toast(
                    data.enabled
                        ? intl.get("DebugSettings.DocExpiry.Enabled").d("Document expiry cycle force run enabled - cycle will run every 60 seconds")
                        : intl.get("DebugSettings.DocExpiry.Disabled").d("Document expiry cycle force run disabled - cycle will run daily at 00:01"),
                    "success"
                );
            } else {
                throw new Error("Update failed");
            }
        } catch (error) {
            Notifications.toast(
                intl.get("DebugSettings.UpdateError").d("Error updating debug mode"),
                "error"
            );
        } finally {
            setSaving(prev => ({ ...prev, doc_expiry: false }));
        }
    };

    const activeCount = [alertSmtpDebug, cfmDebug, sapBuyerSkip, noCheckoutForceRun, docExpiryForceRun].filter(Boolean).length;

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
                <DebugIcon sx={{ fontSize: 32, color: "warning.main" }} />
                <Box>
                    <Typography variant="h6">
                        {intl.get("DebugSettings.Title").d("Debug Tools")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {intl.get("DebugSettings.Subtitle").d("Tools for development and testing")}
                    </Typography>
                </Box>
                {activeCount > 0 && (
                    <Chip
                        label={`${activeCount} ${intl.get("DebugSettings.Active").d("active")}`}
                        color="warning"
                        size="small"
                    />
                )}
            </Box>

            <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    {intl.get("DebugSettings.Warning").d(
                        "These settings are for development and testing only. When enabled, real emails/codes will NOT be sent. Use with caution in production environments."
                    )}
                </Typography>
            </Alert>

            {activeCount > 0 && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    <Typography variant="body2" fontWeight="bold">
                        {intl.get("DebugSettings.ActiveWarning").d(
                            "Warning: Debug modes are active. Some system features are being simulated."
                        )}
                    </Typography>
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <AlertSmtpSettingsCard
                        showTestConnection={false}
                        showConfiguredNote={true}
                        onToggle={handleAlertSmtpToggle}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                <CfmIcon sx={{ mr: 1, color: "info.main" }} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {intl.get("DebugSettings.CfmDebug.Title").d("Debug de CFM")}
                                </Typography>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {intl.get("DebugSettings.CfmDebug.Description").d(
                                    "When enabled, CRM validation will return simulated data without calling the real CFM API. Useful for testing doctor registration flows."
                                )}
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={cfmDebug}
                                        onChange={handleToggleCfmDebug}
                                        disabled={saving.cfm_debug}
                                        color="warning"
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        {cfmDebug
                                            ? intl.get("DebugSettings.CfmDebug.StatusEnabled").d("Validations return simulated data")
                                            : intl.get("DebugSettings.CfmDebug.StatusDisabled").d("Validations use real API")
                                        }
                                    </Typography>
                                }
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                <SapIcon sx={{ mr: 1, color: "info.main" }} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {intl.get("DebugSettings.SapBuyer.Title").d("Skip SAP Buyer Validation")}
                                </Typography>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {intl.get("DebugSettings.SapBuyer.Description").d(
                                    "When enabled, Buyer (Sales Channel) institutions can be created without the CNPJ being registered in SAP. Useful for testing institution registration flows."
                                )}
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={sapBuyerSkip}
                                        onChange={handleToggleSapBuyerSkip}
                                        disabled={saving.sap_buyer}
                                        color="warning"
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        {sapBuyerSkip
                                            ? intl.get("DebugSettings.SapBuyer.StatusEnabled").d("SAP validation skipped for Buyer institutions")
                                            : intl.get("DebugSettings.SapBuyer.StatusDisabled").d("SAP validation required for Buyer institutions")
                                        }
                                    </Typography>
                                }
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                <CycleIcon sx={{ mr: 1, color: "info.main" }} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {intl.get("DebugSettings.NoCheckout.Title").d("No Checkout Cycle - Force Run")}
                                </Typography>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {intl.get("DebugSettings.NoCheckout.Description").d(
                                    "When enabled, the \"No Checkout for Period\" cycle will stop running once a day (at 00:01) and will start running every 60 seconds. Useful for testing the notice generation without waiting until midnight."
                                )}
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={noCheckoutForceRun}
                                        onChange={handleToggleNoCheckoutForceRun}
                                        disabled={saving.no_checkout}
                                        color="warning"
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        {noCheckoutForceRun
                                            ? intl.get("DebugSettings.NoCheckout.StatusEnabled").d("Cycle running every 60 seconds")
                                            : intl.get("DebugSettings.NoCheckout.StatusDisabled").d("Cycle running daily at 00:01")
                                        }
                                    </Typography>
                                }
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                <CycleIcon sx={{ mr: 1, color: "info.main" }} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {intl.get("DebugSettings.DocExpiry.Title").d("Document Expiry Cycle - Force Run")}
                                </Typography>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {intl.get("DebugSettings.DocExpiry.Description").d(
                                    "When enabled, the \"Institution Document Expiry\" cycle will stop running once a day (at 00:01) and will start running every 60 seconds. Useful for testing expiry notifications and SAP deactivations without waiting until midnight."
                                )}
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={docExpiryForceRun}
                                        onChange={handleToggleDocExpiryForceRun}
                                        disabled={saving.doc_expiry}
                                        color="warning"
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        {docExpiryForceRun
                                            ? intl.get("DebugSettings.DocExpiry.StatusEnabled").d("Cycle running every 60 seconds")
                                            : intl.get("DebugSettings.DocExpiry.StatusDisabled").d("Cycle running daily at 00:01")
                                        }
                                    </Typography>
                                }
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                        <CardContent>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                {intl.get("DebugSettings.Reference.Title").d("Quick Reference")}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" paragraph>
                                {intl.get("DebugSettings.Reference.Description").d(
                                    "When debug modes are enabled, check the server console/logs to see simulated outputs."
                                )}
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="caption" color="text.secondary" component="div">
                                        <strong>{intl.get("DebugSettings.Reference.SmtpDebug").d("Debug SMTP:")}</strong>
                                        <br />
                                        {intl.get("DebugSettings.Reference.SmtpDebugDesc").d(
                                            "All system emails (password recovery, registration, approvals, notifications, alerts, MFA codes)"
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="caption" color="text.secondary" component="div">
                                        <strong>{intl.get("DebugSettings.Reference.CfmDebug").d("Debug CFM:")}</strong>
                                        <br />
                                        {intl.get("DebugSettings.Reference.CfmDebugDesc").d(
                                            "CRM validation of doctors with the Federal Council of Medicine"
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="caption" color="text.secondary" component="div">
                                        <strong>{intl.get("DebugSettings.Reference.SapBuyerDebug").d("Skip SAP Buyer:")}</strong>
                                        <br />
                                        {intl.get("DebugSettings.Reference.SapBuyerDebugDesc").d(
                                            "SAP validation when creating Buyer (Sales Channel) institutions"
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="caption" color="text.secondary" component="div">
                                        <strong>{intl.get("DebugSettings.Reference.NoCheckoutDebug").d("No Checkout Force Run:")}</strong>
                                        <br />
                                        {intl.get("DebugSettings.Reference.NoCheckoutDebugDesc").d(
                                            "Forces the No Checkout cycle to run every 60s instead of daily at 00:01"
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="caption" color="text.secondary" component="div">
                                        <strong>{intl.get("DebugSettings.Reference.DocExpiryDebug").d("Doc Expiry Force Run:")}</strong>
                                        <br />
                                        {intl.get("DebugSettings.Reference.DocExpiryDebugDesc").d(
                                            "Forces the Institution Document Expiry cycle to run every 60s instead of daily at 00:01"
                                        )}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    {intl.get("DebugSettings.Footer").d(
                        "This tab is only visible to users with debug permissions (DMS_ACCENTURE role)."
                    )}
                </Typography>
            </Box>
        </Box>
    );
};
